import { Flex, Text, TextField, TextArea, Select, Skeleton } from '@radix-ui/themes'
import {
  FeatureFlag,
  RewardCategory,
  PromotionCampaignPrizeAmountMode,
} from '@Generated/back-office-client/src'
import { produce } from 'immer'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'
import { AMOUNT_MODE_OPTIONS } from '../constants'
import { PromotionCampaignFormData } from '../types'
import { convertFieldNameToLabel } from '../utils'
import '../styles.scss'

type AwardSectionProps = {
  campaign: PromotionCampaignFormData
  canEdit: boolean
  pluginMetadataSchema: Record<string, unknown> | null
  awardTypeOptions: Array<{ value: string; label: string }>
  isLoading: boolean
  onChange: (campaign: PromotionCampaignFormData) => void
}

export const AwardSection = ({
  campaign,
  canEdit,
  pluginMetadataSchema,
  awardTypeOptions,
  isLoading,
  onChange,
}: AwardSectionProps) => {
  const { currentUser } = useSelector((state: RootState) => state.users)
  const amountModeOptions = useMemo(() => {
    const enabledFeatures = currentUser?.enabledFeatures ?? []
    return AMOUNT_MODE_OPTIONS.filter(
      (opt) =>
        opt.value !== PromotionCampaignPrizeAmountMode.TICKET_COST_MULTIPLIER ||
        enabledFeatures.includes(FeatureFlag.ticket_cost_multiplier),
    )
  }, [currentUser?.enabledFeatures])

  if (isLoading) {
    return (
      <Flex direction="column" gap="3">
        <Text size="3" weight="bold">
          Award
        </Text>
        <Skeleton>
          <Flex direction="column" gap="2" style={{ height: '200px' }} />
        </Skeleton>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="3">
      <Text size="3" weight="bold">
        Award
      </Text>

      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Details
        </Text>
        <TextArea
          disabled={!canEdit}
          placeholder="e.g., 100 bonus entries"
          value={campaign.awardConfig?.description || ''}
          onChange={(e) =>
            onChange(
              produce(campaign, (updated: PromotionCampaignFormData) => {
                if (updated.awardConfig) {
                  updated.awardConfig.description = e.target.value
                }
              }),
            )
          }
          rows={2}
        />
      </Flex>

      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Award Type
        </Text>
        <Select.Root
          disabled={!canEdit}
          value={campaign.awardConfig?.type || ''}
          onValueChange={(value) =>
            onChange(
              produce(campaign, (updated: PromotionCampaignFormData) => {
                if (updated.awardConfig) {
                  updated.awardConfig.type = value as RewardCategory
                }
              }),
            )
          }>
          <Select.Trigger />
          <Select.Content>
            {awardTypeOptions.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Flex>

      <Flex gap="2" style={{ flex: 1 }}>
        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">
            Amount Value
          </Text>
          <TextField.Root
            type="number"
            disabled={!canEdit}
            placeholder="0"
            value={campaign.awardConfig?.config.amount?.value || ''}
            onChange={(e) =>
              onChange(
                produce(campaign, (updated: PromotionCampaignFormData) => {
                  if (updated.awardConfig?.config?.amount) {
                    updated.awardConfig.config.amount.value = e.target.value
                      ? parseFloat(e.target.value)
                      : 0
                  }
                }),
              )
            }
          />
        </Flex>
        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">
            Amount Mode
          </Text>
          {amountModeOptions.length > 1 ? (
            <Select.Root
              disabled={!canEdit}
              value={campaign.awardConfig?.config.amount?.mode || 'STATIC'}
              onValueChange={(value) =>
                onChange(
                  produce(campaign, (updated: PromotionCampaignFormData) => {
                    if (updated.awardConfig?.config?.amount) {
                      updated.awardConfig.config.amount.mode =
                        value as PromotionCampaignPrizeAmountMode
                    }
                  }),
                )
              }>
              <Select.Trigger />
              <Select.Content>
                {amountModeOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          ) : (
            <Text size="2">{amountModeOptions[0]?.label ?? ''}</Text>
          )}
        </Flex>
      </Flex>

      {(() => {
        if (!pluginMetadataSchema) {
          return (
            <Flex direction="column" gap="2">
              <Text size="2" color="gray">
                Loading metadata schema...
              </Text>
            </Flex>
          )
        }

        const visibleFields = Object.entries(
          (pluginMetadataSchema.properties as Record<string, { description?: string }>) || {},
        )

        if (visibleFields.length === 0) {
          return (
            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                Award Metadata
              </Text>
              <Text size="2" color="gray">
                No additional configuration required
              </Text>
            </Flex>
          )
        }

        return visibleFields.map(([fieldName, fieldSchema]) => {
          const requiredFields = pluginMetadataSchema.required as string[]
          const isRequired = requiredFields.includes(fieldName)

          return (
            <Flex direction="column" gap="1" key={fieldName}>
              <Text size="2" weight="medium">
                {convertFieldNameToLabel(fieldName)}
                {isRequired && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
              </Text>
              {fieldSchema.description && (
                <Text size="1" color="gray">
                  {fieldSchema.description}
                </Text>
              )}
              <TextField.Root
                disabled={!canEdit}
                value={campaign.awardConfig?.config?.[fieldName] || ''}
                onChange={(e) => {
                  onChange(
                    produce(campaign, (updated: PromotionCampaignFormData) => {
                      if (updated.awardConfig?.config) {
                        updated.awardConfig.config[fieldName] = e.target.value
                      }
                    }),
                  )
                }}
              />
            </Flex>
          )
        })
      })()}
    </Flex>
  )
}
